import React from 'react';
import { Shield, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import Layout from '../../layouts/Layout';

const PrivacyPolicy = () => {
    const lastUpdated = "7th July 2025";

    return (
        <Layout
            title="Privacy Policy"
            description="KGC Innovations Ltd Privacy Policy - How we collect, use, and protect your personal information. GDPR compliant data protection for UK web development services."
            keywords="privacy policy, KGC Innovations, data protection, GDPR compliance, UK web development, personal information, cookies, analytics"
            path="/privacy-policy"
        >
            <div className="min-h-screen bg-white">
                {/* Header */}
                <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center opacity-0 animate-[fadeInUp_0.8s_ease-out_0.2s_forwards]">
                            <div className="inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium mb-8">
                                <Shield className="w-4 h-4 mr-2" />
                                Legal Compliance
                            </div>
                            
                            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                                Privacy Policy
                            </h1>
                            
                            <p className="text-xl text-slate-600 mb-4">
                                How we collect, use, and protect your personal information
                            </p>
                            
                            <div className="flex items-center justify-center text-slate-500">
                                <Calendar className="w-4 h-4 mr-2" />
                                Last updated: {lastUpdated}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Content */}
                <section className="py-16 bg-white">
                    <div className="max-w-4xl mx-auto px-6">
                        <div className="prose prose-lg prose-slate max-w-none">
                            
                            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 mb-12">
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">Introduction</h2>
                                <p className="text-slate-700 leading-relaxed">
                                    KGC Innovations Ltd ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website kgcinnovations.com or use our services.
                                </p>
                            </div>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">1. Information We Collect</h2>
                            
                            <h3 className="text-xl font-semibold text-slate-900 mb-4">Personal Information You Provide</h3>
                            <p>When you contact us through our website or engage our services, we may collect:</p>
                            <ul className="list-disc ml-6 mb-6">
                                <li>Name and contact information (email address, phone number)</li>
                                <li>Company name and business information</li>
                                <li>Project requirements and technical specifications</li>
                                <li>Any additional information you choose to share</li>
                                <li>Documents or files you upload through our contact forms</li>
                            </ul>

                            <h3 className="text-xl font-semibold text-slate-900 mb-4">Automatically Collected Information</h3>
                            <p>We automatically collect certain information when you visit our website:</p>
                            <ul className="list-disc ml-6 mb-8">
                                <li>IP address and device information</li>
                                <li>Browser type and version</li>
                                <li>Pages visited and time spent on our site</li>
                                <li>Referring website information</li>
                                <li>User preferences and settings</li>
                            </ul>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">2. How We Use Your Information</h2>
                            <p>We use the information we collect to:</p>
                            <ul className="list-disc ml-6 mb-8">
                                <li>Provide, maintain, and improve our services</li>
                                <li>Respond to your inquiries and provide customer support</li>
                                <li>Process payments and manage our business relationship</li>
                                <li>Send you service-related communications</li>
                                <li>Analyze website usage to improve user experience</li>
                                <li>Comply with legal obligations</li>
                                <li>Protect against fraud and ensure security</li>
                            </ul>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">3. Legal Basis for Processing (GDPR)</h2>
                            <p>Under GDPR, we process your personal data based on the following legal grounds:</p>
                            <ul className="list-disc ml-6 mb-8">
                                <li><strong>Consent:</strong> When you provide explicit consent for specific processing activities</li>
                                <li><strong>Contract:</strong> When processing is necessary for performing our services</li>
                                <li><strong>Legitimate Interest:</strong> For business operations, security, and website analytics</li>
                                <li><strong>Legal Obligation:</strong> When required by law</li>
                            </ul>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">4. Information Sharing and Disclosure</h2>
                            <p>We do not sell, trade, or otherwise transfer your personal information to third parties except:</p>
                            
                            <h3 className="text-xl font-semibold text-slate-900 mb-4">Service Providers</h3>
                            <ul className="list-disc ml-6 mb-6">
                                <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
                                <li><strong>Payment Processors:</strong> Stripe and GoCardless for processing payments</li>
                                <li><strong>Hosting Providers:</strong> For website hosting and data storage</li>
                            </ul>

                            <h3 className="text-xl font-semibold text-slate-900 mb-4">Legal Requirements</h3>
                            <p className="mb-8">We may disclose information when required by law or to protect our rights, property, or safety.</p>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">5. Your Rights Under GDPR</h2>
                            <p>If you are located in the EU/UK, you have the following rights:</p>
                            <ul className="list-disc ml-6 mb-6">
                                <li><strong>Right of Access:</strong> Request copies of your personal data</li>
                                <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
                                <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
                                <li><strong>Right to Restrict Processing:</strong> Request limitation of processing</li>
                                <li><strong>Right to Data Portability:</strong> Request transfer of your data</li>
                                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
                            </ul>
                            <p className="mb-8">To exercise these rights, contact us at legal@kgcinnovations.com</p>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">6. Data Security</h2>
                            <p>We implement appropriate technical and organizational measures to protect your personal information:</p>
                            <ul className="list-disc ml-6 mb-8">
                                <li>SSL encryption for data transmission</li>
                                <li>Secure hosting infrastructure</li>
                                <li>Regular security assessments</li>
                                <li>Access controls and authentication</li>
                                <li>Employee training on data protection</li>
                            </ul>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">7. Data Retention</h2>
                            <p>We retain personal information for as long as necessary to:</p>
                            <ul className="list-disc ml-6 mb-6">
                                <li>Provide our services and maintain our business relationship</li>
                                <li>Comply with legal obligations (typically 7 years for business records)</li>
                                <li>Resolve disputes and enforce agreements</li>
                            </ul>
                            <p className="mb-8">Website analytics data is retained for 26 months in Google Analytics.</p>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">8. Contact Information</h2>
                            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                                <h3 className="text-xl font-semibold text-slate-900 mb-4">Data Controller</h3>
                                <p className="mb-6">
                                    KGC Innovations Ltd<br />
                                    Company Registration: 16570829<br />
                                    Registered Office: Unit A, 82 James Carter Road, Mildenhall, IP28 7DE, England<br />
                                    Email: legal@kgcinnovations.com<br />
                                    Phone: +44 (0) 20 7046 0289
                                </p>
                                
                                <h3 className="text-xl font-semibold text-slate-900 mb-4">Supervisory Authority</h3>
                                <p>If you have concerns about our data handling, you can contact the Information Commissioner's Office (ICO) at ico.org.uk</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Contact Section */}
                <section className="py-16 bg-slate-50">
                    <div className="max-w-4xl mx-auto px-6 text-center">
                        <h2 className="text-3xl font-bold text-slate-900 mb-6">Questions About Our Privacy Policy?</h2>
                        <p className="text-lg text-slate-600 mb-8">
                            We're committed to transparency. Contact us if you have any privacy-related questions.
                        </p>
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="bg-white rounded-xl p-6 border border-slate-200">
                                <Mail className="w-8 h-8 text-slate-900 mx-auto mb-3" />
                                <div className="font-semibold text-slate-900 mb-1">Compliance</div>
                                <div className="text-slate-600">legal@kgcinnovations.com</div>
                            </div>
                            <div className="bg-white rounded-xl p-6 border border-slate-200">
                                <Phone className="w-8 h-8 text-slate-900 mx-auto mb-3" />
                                <div className="font-semibold text-slate-900 mb-1">Phone</div>
                                <div className="text-slate-600">+44 (0) 20 7046 0289</div>
                            </div>
                            <div className="bg-white rounded-xl p-6 border border-slate-200">
                                <MapPin className="w-8 h-8 text-slate-900 mx-auto mb-3" />
                                <div className="font-semibold text-slate-900 mb-1">Registered Office</div>
                                <div className="text-slate-600 text-sm">England & Wales</div>
                            </div>
                        </div>
                    </div>
                </section>

                <style jsx>{`
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    .animate-[fadeInUp_0.8s_ease-out_0.2s_forwards] {
                        animation: fadeInUp 0.8s ease-out 0.2s forwards;
                    }
                `}</style>
            </div>
        </Layout>
    );
};

export default PrivacyPolicy;